/* Разделы карточки заказа: клиент и авто, время и пост, услуги, скидка, итоги, комментарий, история. */
import { useEffect, useRef, useState } from "react";
import {
  CLASS_MULT,
  MIN,
  SOURCE,
  STATUS,
  at,
  dateTime,
  dayBounds,
  dayLabel,
  duration,
  initials,
  maxDiscountFor,
  money,
  num,
  orderEnd,
  phoneFmt,
  priceFor,
  round,
  time,
  type Customer,
  type Offer,
  type Order,
  type Totals,
  type Vehicle,
} from "@okno/shared";
import { api } from "../../lib/api";
import { useActions } from "../../lib/actions";
import { useSession } from "../../lib/session";
import { Icon } from "../../ui/Icon";
import { Plate, carLine } from "../../ui/kit";
import { useToast } from "../../ui/toast";
import { currencySign, useOrderApi } from "./useOrder";

function useServiceName() {
  const { boot } = useSession();
  return (sid: string) => boot.services.find((s) => s.id === sid)?.name || "Услуга";
}

export function ClientSection({ customer: c, vehicle: v, offer }: { customer: Customer; vehicle: Vehicle; offer: Offer | null }) {
  const { openClient } = useActions();
  const name = useServiceName();
  return (
    <section className="panel panel-body stack stack-sm">
      <div className="row" style={{ gap: 12 }}>
        <span className="avatar">{initials(c.name)}</span>
        <div className="grow" style={{ flex: 1, minWidth: 0 }}>
          <div className="cell-main">{c.name}</div>
          <a className="cell-sub" href={"tel:+" + c.phone}>
            {phoneFmt(c.phone)}
          </a>
        </div>
        <button className="btn btn-sm" onClick={() => openClient(c.id)}>
          Карточка
        </button>
      </div>
      <div className="row">
        <Plate plate={v.plate} />
        <span className="muted" style={{ fontSize: 14 }}>
          {carLine(v) + (v.mileage ? ", " + num(v.mileage) + " км" : "")}
        </span>
      </div>
      {offer ? (
        <div className="callout callout-signal">
          <Icon name="zap" size={16} />
          <span>
            Заказ создан по смарт-предложению: {name(offer.serviceId)} за {money(offer.price)}
          </span>
        </div>
      ) : null}
    </section>
  );
}

/** Пост, исполнитель, время начала (перенос) и план. */
export function TimeBaySection({ order: o, editable, canAssign }: { order: Order; editable: boolean; canAssign: boolean }) {
  const { boot, labels } = useSession();
  const toast = useToast();
  const { path, run } = useOrderApi(o.id);
  const [tm, setTm] = useState(time(o.start));
  const busy = useRef(false);
  useEffect(() => setTm(time(o.start)), [o.start]);
  const workers = boot.staff.filter((s) => s.role === "worker" && (s.active || s.id === o.workerId));

  const move = async (bayId: string, value: string) => {
    const revert = (msg?: string) => {
      if (msg) toast(msg, "bad");
      setTm(time(o.start));
    };
    const [hh, mm] = (value || "").split(":").map(Number);
    const start = at(o.start, hh, mm || 0);
    if (!value || !Number.isFinite(start)) return revert("Укажите время начала");
    if (start === o.start && bayId === o.bayId) return;
    const { open, close } = dayBounds({ location: boot.location }, o.start);
    if (start < open || start + o.dur * MIN > close) return revert("Точка работает с " + time(open) + " до " + time(close));
    if (start < Date.now() && start !== o.start) return revert("Нельзя перенести запись в прошлое");
    const bay = boot.bays.find((b) => b.id === bayId);
    if (!bay) return revert();
    const cats = o.items.map((it) => boot.services.find((s) => s.id === it.sid)?.category);
    if (!cats.every((c) => !c || bay.cats.includes(c))) return revert(bay.name + " не подходит для этих услуг");
    // Enter и потеря фокуса не должны отправить перенос дважды
    if (busy.current) return;
    busy.current = true;
    const res = await run(() => api.post<Order>(path + "/reschedule", { bayId, start }));
    busy.current = false;
    if (res) toast("Запись перенесена на " + time(start) + ", " + bay.name);
    else revert();
  };

  const setWorker = (workerId: string) => void run(() => api.patch<Order>(path, { workerId: workerId || null }));

  return (
    <section className="form-grid">
      <label className="field">
        <span>{labels.bayWord}</span>
        <select className="select" value={o.bayId} disabled={!editable} onChange={(e) => void move(e.target.value, tm)}>
          {boot.bays
            .filter((b) => b.active || b.id === o.bayId)
            .map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
        </select>
      </label>
      <label className="field">
        <span>Исполнитель</span>
        <select className="select" value={o.workerId || ""} disabled={!canAssign} onChange={(e) => setWorker(e.target.value)}>
          <option value="">Не назначен</option>
          {workers.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Начало</span>
        <input
          className="input"
          type="time"
          step={600}
          value={tm}
          disabled={!editable}
          onChange={(e) => setTm(e.target.value)}
          onBlur={() => void move(o.bayId, tm)}
          onKeyDown={(e) => e.key === "Enter" && void move(o.bayId, tm)}
        />
      </label>
      <div className="field">
        <span>План</span>
        <div className="input" style={{ display: "flex", alignItems: "center", background: "var(--panel-2)" }}>
          {dayLabel(o.start) + ", " + time(o.start) + "–" + time(orderEnd(o)) + " · " + duration(o.dur)}
        </div>
      </div>
    </section>
  );
}

export function ServicesSection({ order: o, vehicle: v, editable }: { order: Order; vehicle: Vehicle; editable: boolean }) {
  const { boot, can, vertical } = useSession();
  const { path, run } = useOrderApi(o.id);
  const name = useServiceName();
  const finance = can("finance");
  const bay = boot.bays.find((b) => b.id === o.bayId);
  const used = o.items.map((x) => x.sid);
  const avail = editable ? boot.services.filter((s) => s.active && !used.includes(s.id) && !!bay && bay.cats.includes(s.category)) : [];

  const remove = (itemId: string | undefined) => {
    if (itemId) void run(() => api.del<Order>(path + "/items/" + itemId));
  };
  const add = (serviceId: string) => {
    if (serviceId) void run(() => api.post<Order>(path + "/items", { serviceId }));
  };

  return (
    <section>
      <div className="section-title">Услуги</div>
      <div className="panel">
        <div className="list">
          {o.items.map((it, i) => (
            <div className="list-item" key={it.id || it.sid + i}>
              <div className="grow">
                <div className="cell-main">{name(it.sid)}</div>
                <div className="cell-sub">
                  {duration(it.dur)}
                  {finance ? " · мин. цена " + money(it.floor) : ""}
                </div>
              </div>
              <span className="num">{money(it.price)}</span>
              {editable && o.items.length > 1 ? (
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => remove(it.id)} aria-label="Убрать услугу">
                  <Icon name="x" size={16} />
                </button>
              ) : null}
            </div>
          ))}
          {avail.length ? (
            <div className="list-item">
              <select className="select" value="" onChange={(e) => add(e.target.value)} aria-label="Добавить услугу">
                <option value="">Добавить услугу…</option>
                {avail.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name + " · " + money(priceFor(s, v.cls, CLASS_MULT[vertical]).price)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/** Скидка не ниже минимальной цены услуг: подсказка в UI, сервер проверяет повторно. */
export function DiscountSection({ order: o, t }: { order: Order; t: Totals }) {
  const { path, run } = useOrderApi(o.id);
  const [val, setVal] = useState(o.discount ? String(o.discount) : "");
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setVal(o.discount ? String(o.discount) : ""), [o.discount]);
  const max = maxDiscountFor(o.items);

  const apply = (v: number) => {
    if (v > max) {
      setErr("Максимум " + money(max) + ": итог не может быть ниже минимальной цены " + money(t.floor));
      return;
    }
    setErr(null);
    if (v === (o.discount || 0)) return;
    void run(() => api.post<Order>(path + "/discount", { discount: v }));
  };
  const fromInput = () => apply(Math.max(0, parseInt(val.replace(/\D/g, ""), 10) || 0));
  const byPct = (p: number) => {
    const v = round((t.subtotal * p) / 100, 100);
    setVal(v ? String(v) : "");
    apply(v);
  };

  return (
    <section className="field">
      <span>Скидка, {currencySign()}</span>
      <div className="row" style={{ flexWrap: "nowrap" }}>
        <input
          className="input"
          inputMode="numeric"
          value={val}
          placeholder="0"
          style={{ maxWidth: 160 }}
          aria-invalid={err ? true : undefined}
          aria-label="Скидка"
          onChange={(e) => setVal(e.target.value)}
          onBlur={fromInput}
          onKeyDown={(e) => e.key === "Enter" && fromInput()}
        />
        <button className="btn btn-sm" onClick={() => byPct(5)}>
          5%
        </button>
        <button className="btn btn-sm" onClick={() => byPct(10)}>
          10%
        </button>
        <button className="btn btn-sm" onClick={() => byPct(0)}>
          Без скидки
        </button>
      </div>
      <span className={err ? "error" : "hint"}>{err || "Итог не может быть ниже минимальной цены услуг: " + money(t.floor)}</span>
    </section>
  );
}

export function TotalsSection({ t }: { t: Totals }) {
  const { can } = useSession();
  return (
    <section className="panel panel-body">
      <dl className="kv">
        <dt>Сумма услуг</dt>
        <dd className="num">{money(t.subtotal)}</dd>
        {t.discount ? (
          <>
            <dt>Скидка</dt>
            <dd className="num">−{money(t.discount)}</dd>
          </>
        ) : null}
        <dt>Итого</dt>
        <dd className="num" style={{ fontSize: 17 }}>
          {money(t.total)}
        </dd>
        <dt>Оплачено</dt>
        <dd className="num">{money(t.paid)}</dd>
        <dt>К оплате</dt>
        <dd className="num" style={{ color: t.due ? "var(--st-bad)" : "var(--st-ready)" }}>
          {money(t.due)}
        </dd>
        {can("finance") ? (
          <>
            <dt>Маржа</dt>
            <dd className="num">{money(t.margin)}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}

export function CommentSection({ order: o, disabled }: { order: Order; disabled: boolean }) {
  const toast = useToast();
  const { path, run } = useOrderApi(o.id);
  const [text, setText] = useState(o.comment || "");
  useEffect(() => setText(o.comment || ""), [o.comment]);
  const save = async () => {
    const comment = text.trim();
    if (comment === (o.comment || "")) return;
    const res = await run(() => api.patch<Order>(path, { comment }));
    if (res) toast("Комментарий сохранён");
  };
  return (
    <label className="field">
      <span>Комментарий</span>
      <textarea
        className="textarea"
        placeholder="Например: клиент просит не трогать детское кресло"
        value={text}
        disabled={disabled}
        maxLength={2000}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => void save()}
      />
    </label>
  );
}

export function HistorySection({ order: o }: { order: Order }) {
  const hist = [{ s: "created" as const, at: o.createdAt, by: SOURCE[o.source] || "" }, ...(o.history || [])];
  return (
    <section>
      <div className="section-title">История</div>
      <ol className="timeline">
        {hist.map((h, i) => (
          <li key={i}>
            <span>{h.s === "created" ? "Создан" : STATUS[h.s]?.label || h.s}</span>
            <span className="muted">{dateTime(h.at) + " · " + h.by}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}
